import { computed, ref } from 'vue'

export type ToastAction = {
  label: string
  onClick: () => void
}

export type ToastOptions = {
  text: string
  variant?: 'success' | 'error'
  duration?: number
  action?: ToastAction
  closable?: boolean
}

export type ToastVariantOptions = Omit<ToastOptions, 'text' | 'variant'>

export type Toast = {
  id: string
  text: string
  variant: 'success' | 'error'
  action?: ToastAction
  closable: boolean
}

export const TOAST_DEFAULT_DURATION = 6000
const MAX_TOASTS = 5

const state = ref<Toast[]>([])
const timers = new Map<string, ReturnType<typeof setTimeout>>()

export function useToast() {
  const toasts = computed(() => state.value)

  function add(options: ToastOptions): string {
    const id = crypto.randomUUID()
    const duration = options.duration ?? TOAST_DEFAULT_DURATION

    const toast: Toast = {
      id,
      text: options.text,
      variant: options.variant ?? 'success',
      action: options.action,
      closable: !!options.closable,
    }

    // Remove the oldest if at max capacity
    if (state.value.length >= MAX_TOASTS) {
      const oldest = state.value[0]
      if (oldest) {
        clearTimer(oldest.id)
      }
      state.value = state.value.slice(1)
    }

    state.value = [...state.value, toast]

    // Auto-dismiss unless duration is 0
    if (duration > 0) {
      const timer = setTimeout(() => {
        remove(id)
      }, duration)
      timers.set(id, timer)
    }

    return id
  }

  function success(text: string, options?: ToastVariantOptions): string {
    return add({ ...options, text, variant: 'success' })
  }

  function error(text: string, options?: ToastVariantOptions): string {
    return add({ ...options, text, variant: 'error' })
  }

  function clearTimer(id: string): void {
    const timer = timers.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.delete(id)
    }
  }

  function remove(id: string): void {
    clearTimer(id)
    state.value = state.value.filter((toast) => toast.id !== id)
  }

  function clear(): void {
    timers.forEach((timer) => clearTimeout(timer))
    timers.clear()
    state.value = []
  }

  return {
    toasts,
    add,
    success,
    error,
    remove,
    clear,
  }
}
